<template>
  <div class="flex items-center justify-end relative">
    <p class="flex text-[16px] text-[#414141] font-['Open_Sans'] font-normal leading-[160%] tracking-[0%] align-middle mr-2.5">
      <img class="mr-2" src="@/assets/assignee-icon.svg" alt="Assignees Icon" />
      Assignees
    </p>
    <ul class="flex -space-x-2">
      <li v-for="(member, index) in assignedMembers" :key="member.id" class="relative" :style="{ zIndex: assignedMembers.length - index }">
        <div class="h-[28px] w-[28px] rounded-full">
          <img v-if="member.logo_url" class="h-full w-full rounded-full object-cover" :src="member.logo_url" :alt="member.name" />
          <div v-else class="h-full w-full border-[1px] rounded-full flex items-center justify-center" :style="{ backgroundColor: '#EDEFEE' }">
            <span class="text-[#414141] font-['Open_Sans'] font-semibold text-[12px] leading-[150%] tracking-[0%]">{{ getInitials(member) }}</span>
          </div>
        </div>
      </li>
      <li class="relative" :style="{ zIndex: 0 }">
        <div class="relative group">
          <img
            class="h-[28px] w-[28px] rounded-full cursor-pointer hover:opacity-80 transition-opacity"
            :src="addIcon"
            alt="Add Assignee"
            @click="toggleUserList"
            ref="toggleButton"
          />
          <!-- Tooltip -->
          <div class="absolute right-full top-1/2 transform -translate-y-1/2 mr-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
            Click the plus sign to assign a user to this section
            <!-- Tooltip arrow -->
            <div class="absolute top-1/2 left-full transform -translate-y-1/2 w-0 h-0 border-t-4 border-b-4 border-l-4 border-transparent border-l-gray-800"></div>
          </div>
        </div>
      </li>
    </ul>

    <!-- User Selection Dropdown -->
    <div v-if="showUserList" ref="dropdownRef" class="absolute right-0 top-10 bg-white border border-primary-300 rounded-[12px] shadow-[0px_0px_9.3px_0px_#00000040] p-6 w-[400px] z-50">
      <!-- Header -->
      <div class="flex items-center justify-between mb-6">
        <h3 class="font-['Open_Sans'] font-semibold text-[#00423D] text-[16px] leading-[160%] tracking-[0%]">Select Assignees</h3>
        <button @click="toggleUserList" class="hover:opacity-80 transition-opacity">
          <svg
            class="w-6 h-6"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="#00423D"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>

      <div class="max-h-[calc(100vh-382px)] overflow-y-auto pr-2">
        <ul v-if="!isLoading" class="space-y-3">
          <li v-for="user in organizationUsers" :key="user.id" 
              class="flex justify-between items-center py-2.5 px-3 hover:bg-gray-50 rounded-lg transition-colors border border-transparent hover:border-primary-300">
            <div class="flex items-center flex-1 min-w-0">
              <div class="h-10 w-10 rounded-full flex-shrink-0 mr-3">
                <img v-if="user.logo_url" class="h-full w-full rounded-full object-cover" :src="user.logo_url" :alt="user.name" />
                <div v-else class="h-full w-full border-[1px] rounded-full flex items-center justify-center" :style="{ backgroundColor: '#EDEFEE' }">
                  <span class="text-[#414141] font-['Open_Sans'] font-semibold text-[16px] leading-[150%] tracking-[0%]">{{ getInitials(user) }}</span>
                </div>
              </div>
              <div class="min-w-0 flex-1">
                <p class="font-minion font-semibold text-primary-400 text-[16px] truncate">{{ user.name || user.email }}</p>
                <p class="font-minion text-sm text-placeholder truncate">{{ user.email }}</p>
              </div>
            </div>
            <button
              class="text-sm px-4 py-1.5 rounded-lg font-['Open_Sans'] transition-colors flex-shrink-0 ml-3"
              :class="isAssigned(user.id) ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-primary-400 hover:bg-primary-300 text-white'"
              @click="toggleAssignee(user)"
            >
              {{ isAssigned(user.id) ? "Remove" : "Add" }}
            </button>
          </li>
        </ul>
        <div v-else class="flex justify-center items-center py-12">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-400"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from "vue";
import addIcon from "@/assets/plus-black-icon.svg";
import { useAuthStore } from "@/stores/auth";
import { useRoutesManagement } from "@/stores/routesManagement";
import { useRoute } from "vue-router";
import global from "@/global";

const authStore = useAuthStore();
const routesManagement = useRoutesManagement();
const route = useRoute();
const organizationUsers = ref([]);
const assignedMembers = ref([]);
const showUserList = ref(false);
const dropdownRef = ref(null);
const toggleButton = ref(null);
const isLoading = ref(false);

// Get current route path
const currentRoutePath = computed(() => route.path);

// Get assigned members for current route
const currentRouteAssignees = computed(() => {
  const assigneeIds = routesManagement.getAssigneesForRoute(currentRoutePath.value);
  // Ensure assigneeIds is an array before mapping
  const ids = Array.isArray(assigneeIds) ? assigneeIds : [];
  return ids.map(id => organizationUsers.value.find(user => user.id === id)).filter(Boolean);
});

// Watch for changes in currentRouteAssignees
watch(currentRouteAssignees, (newAssignees) => {
  assignedMembers.value = newAssignees;
}, { immediate: true });

// Watch for changes in organizationUsers to update assignedMembers
watch(organizationUsers, () => {
  assignedMembers.value = currentRouteAssignees.value;
}, { immediate: true });

// Add onMounted hook to fetch users when component is mounted
onMounted(() => {
  fetchOrganizationUsers();
});

// Update fetchOrganizationUsers to be more robust
const fetchOrganizationUsers = async () => {
  if (!authStore.user || !authStore.user.accessToken) {
    console.log("Cannot fetch organization users: No authenticated user");
    return;
  }

  try {
    isLoading.value = true;
    const res = await fetch(`${global.API_URL}/users/team-members`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
        "Content-Type": "application/json",
      },
    });
    const data = await res.json();
    
    if (data.data) {
      organizationUsers.value = data.data.map(member => ({
        id: member.id,
        name: member.first_name && member.last_name 
          ? `${member.first_name} ${member.last_name}` 
          : member.email,
        email: member.email,
        logo_url: member.logo_url,
        first_name: member.first_name,
        last_name: member.last_name
      }));

      // Update assigned members based on current route
      assignedMembers.value = currentRouteAssignees.value;
    }
  } catch (err) {
    console.error("Failed to fetch organization users:", err);
    assignedMembers.value = [];
  } finally {
    isLoading.value = false;
  }
};

// Update toggleUserList to only handle the popup visibility
const toggleUserList = () => {
  showUserList.value = !showUserList.value;
  if (showUserList.value) {
    document.addEventListener("click", closeOnOutsideClick);
  } else {
    document.removeEventListener("click", closeOnOutsideClick);
  }
};

const closeOnOutsideClick = (event) => {
  if (
    dropdownRef.value &&
    !dropdownRef.value.contains(event.target) &&
    toggleButton.value &&
    !toggleButton.value.contains(event.target)
  ) {
    showUserList.value = false;
    document.removeEventListener("click", closeOnOutsideClick);
  }
};

const isAssigned = (id) => {
  return assignedMembers.value.some((member) => member.id === id);
};

const toggleAssignee = async (user) => {
  try {
    if (isAssigned(user.id)) {
      // Remove assignee
      await routesManagement.removeAssignee(currentRoutePath.value, user.id);
    } else {
      // Add assignee
      await routesManagement.updateAssignee(currentRoutePath.value, user.id);
    }
  } catch (error) {
    console.error("Failed to toggle assignee:", error);
  }
};

const getInitials = (member) => {
  // Priority 1: Use first and last name
  if (member.first_name && member.last_name) {
    return `${member.first_name[0]}${member.last_name[0]}`.toUpperCase();
  }

  // Priority 2: If we don't have first_name and last_name, use email
  if (member.email) {
    const emailPart = member.email.split('@')[0];
    
    // Try to extract initials from email - check if email contains dots, underscores, or hyphens
    // that might separate first and last name
    const separators = ['.', '_', '-'];
    for (const separator of separators) {
      if (emailPart.includes(separator)) {
        const parts = emailPart.split(separator);
        if (parts.length >= 2 && parts[0].length > 0 && parts[1].length > 0) {
          return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
        }
      }
    }
    
    // If we couldn't extract initials in a first name/last name format,
    // simply use the first two characters of the email
    return emailPart.substring(0, 2).toUpperCase();
  }
  
  // Fallback if no email or name is available
  return '??';
};

// Cleanup event listener when component unmounts
onUnmounted(() => {
  document.removeEventListener("click", closeOnOutsideClick);
});
</script>

<style scoped>
/* These are for the primary colors if not defined globally */
:root {
--primary-300: #009C87;
--primary-400: #00423D;
}

.bg-primary-300 {
background-color: var(--primary-300);
}

.text-primary-300 {
color: var(--primary-300);
}

.text-primary-400 {
color: var(--primary-400);
}

.border-primary-300 {
border-color: var(--primary-300);
}

.text-placeholder {
color: #6B7280;
}

/* Custom scrollbar styles */
.overflow-y-auto::-webkit-scrollbar {
width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
background: #f1f1f1;
border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
background: #888;
border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
background: #555;
}
</style>

